import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Avatar, MenuItem, MenuList, Paper, Typography } from '@mui/material';
import { withMediaToken } from '../../../utils/media';
import { colorFromSeed } from '../../../utils/colorFromSeed';

export interface MentionItem {
  id: number;
  label: string;
}

export interface MentionListProps {
  items: MentionItem[];
  /** 선택 확정 — tiptap suggestion 이 주입. {id,label} 으로 멘션 노드 삽입. */
  command: (item: { id: string; label: string }) => void;
}

export interface MentionListHandle {
  onKeyDown: (args: { event: KeyboardEvent }) => boolean;
}

/** @ 입력 시 뜨는 사용자 선택 팝업. 위/아래/Enter 키보드 네비게이션 지원. */
const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => setSelectedIndex(0), [items]);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) command({ id: String(item.id), label: item.label });
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((i) => (i + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <Paper elevation={4} sx={{ px: 1.5, py: 1 }}>
          <Typography variant="body2" color="text.disabled">
            일치하는 사용자가 없습니다
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper elevation={4} sx={{ maxHeight: 240, overflowY: 'auto', minWidth: 200 }}>
        <MenuList dense disablePadding>
          {items.map((item, index) => (
            <MenuItem
              key={item.id}
              selected={index === selectedIndex}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => selectItem(index)}
            >
              <Avatar
                src={withMediaToken(`/v0.1/auth/users/${item.id}/profile-image`)}
                sx={{ width: 22, height: 22, fontSize: '0.7rem', mr: 1, bgcolor: colorFromSeed(item.label), color: '#fff' }}
                imgProps={{
                  onError: (e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  },
                }}
              >
                {item.label.charAt(0)}
              </Avatar>
              <Typography variant="body2">{item.label}</Typography>
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    );
  },
);

MentionList.displayName = 'MentionList';

export default MentionList;
